import { Fragment, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Menu from "lucide-react/dist/esm/icons/menu";
import X from "lucide-react/dist/esm/icons/x";
import ChevronDown from "lucide-react/dist/esm/icons/chevron-down";

import { motion, AnimatePresence } from "framer-motion";
import logoLuca from "@/assets/logo-luca.png";
import LanguageSelector from "./LanguageSelector";

type NavItemType = {
  label: string;
  path: string;
  external?: boolean;
  children?: { label: string; path: string }[];
};

const navItems: NavItemType[] = [
  { label: "Ferienwohnungen", path: "/" },
  { label: "Umgebung", path: "/wissenswertes/die-umgebung" },
  { label: "Bildergalerie", path: "/wissenswertes/bildergalerie" },
  { label: "Stadt Cuxhaven", path: "https://www.nordseeheilbad-cuxhaven.de", external: true },
  { label: "Kontakt", path: "/kontakt" },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border shadow-sm">
      <div className="container-narrow mx-auto flex items-center justify-between h-16 sm:h-20 px-4 sm:px-6">
        {/* Logo - left aligned */}
        <Link to="/" className="flex items-center gap-2 min-w-0" aria-label="Ferienwohnungen Luca — Startseite">
          <img
            src={logoLuca}
            alt=""
            aria-hidden="true"
            width={48}
            height={48}
            className="h-10 sm:h-12 w-auto shrink-0"
            loading="eager"
            decoding="async"
          />
          <span className="font-display text-base sm:text-lg font-semibold text-primary truncate">
            Ferienwohnungen Luca
          </span>
        </Link>

        {/* Right nav - desktop */}
        <nav className="hidden lg:flex items-stretch h-12">
          {navItems.map((item, idx) => (
            <Fragment key={item.path}>
              {idx > 0 && (
                <span
                  aria-hidden="true"
                  className="self-center h-5 w-px bg-border shrink-0"
                />
              )}
              <NavItem item={item} isActive={isActive} openDropdown={openDropdown} setOpenDropdown={setOpenDropdown} />
            </Fragment>
          ))}
          <span aria-hidden="true" className="self-center h-5 w-px bg-border shrink-0" />
          <div className="flex items-center pl-2">
            <LanguageSelector />
          </div>
        </nav>


        {/* Mobile controls */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-foreground"
            aria-label="Menü öffnen"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-background border-t border-border"
          >
            <nav className="px-4 py-4 space-y-1">
              {navItems.map((item) => (
                <MobileNavItem key={item.path} item={item} isActive={isActive} close={() => setMobileOpen(false)} />
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};



const NavItem = ({
  item,
  isActive,
  openDropdown,
  setOpenDropdown,
}: {
  item: NavItemType;
  isActive: (p: string) => boolean;
  openDropdown: string | null;
  setOpenDropdown: (v: string | null) => void;
}) => {
  const hasChildren = item.children && item.children.length > 0;

  const active = !item.external && isActive(item.path);
  const linkClass = `group relative flex items-center justify-center gap-1 w-32 h-full px-4 text-sm font-medium font-body transition-colors ${
    active ? "text-accent" : "text-foreground hover:text-accent"
  }`;
  const underline = (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute left-3 right-3 bottom-2 h-[2px] bg-accent rounded-full origin-left transition-transform duration-300 ease-out ${
        active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
      }`}
    />
  );

  return (
    <div
      className="relative flex items-stretch"
      onMouseEnter={() => hasChildren && setOpenDropdown(item.label)}
      onMouseLeave={() => setOpenDropdown(null)}
    >
      {item.external ? (
        <a
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClass}
        >
          <span>{item.label}</span>
          {underline}
        </a>
      ) : (
        <Link
          to={hasChildren ? item.children![0].path : item.path}
          className={linkClass}
        >
          <span>{item.label}</span>
          {hasChildren && <ChevronDown className="w-3.5 h-3.5" />}
          {underline}
        </Link>
      )}


      <AnimatePresence>
        {hasChildren && openDropdown === item.label && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-1 w-56 glass-card rounded-lg py-2 shadow-lg"
          >
            {item.children!.map((child) =>
              child.path.startsWith("http") ? (
                <a
                  key={child.path}
                  href={child.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-2.5 text-sm font-body text-foreground hover:text-accent hover:bg-accent/5 transition-colors"
                >
                  {child.label}
                </a>
              ) : (
                <Link
                  key={child.path}
                  to={child.path}
                  className="block px-4 py-2.5 text-sm font-body text-foreground hover:text-accent hover:bg-accent/5 transition-colors"
                >
                  {child.label}
                </Link>
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileNavItem = ({
  item,
  isActive,
  close,
}: {
  item: NavItemType;
  isActive: (p: string) => boolean;
  close: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  return (
    <div>
      <div className="flex items-center">
        {item.external ? (
          <a
            href={item.path}
            target="_blank"
            rel="noopener noreferrer"
            onClick={close}
            className="flex-1 flex items-center justify-between px-3 py-3 rounded-lg text-base font-body font-medium text-foreground"
          >
            {item.label}
          </a>
        ) : (
          <Link
            to={hasChildren ? "#" : item.path}
            onClick={(e) => {
              if (hasChildren) {
                e.preventDefault();
                setOpen(!open);
              } else {
                close();
              }
            }}
            className={`flex-1 flex items-center justify-between px-3 py-3 rounded-lg text-base font-body font-medium ${
              isActive(item.path) ? "text-accent" : "text-foreground"
            }`}
          >
            {item.label}
            {hasChildren && (
              <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
            )}
          </Link>
        )}
      </div>
      {hasChildren && open && (
        <div className="pl-4 space-y-1">
          {item.children!.map((child) =>
            child.path.startsWith("http") ? (
              <a
                key={child.path}
                href={child.path}
                target="_blank"
                rel="noopener noreferrer"
                onClick={close}
                className="block px-3 py-2.5 text-sm font-body text-muted-foreground hover:text-accent"
              >
                {child.label}
              </a>
            ) : (
              <Link
                key={child.path}
                to={child.path}
                onClick={close}
                className="block px-3 py-2.5 text-sm font-body text-muted-foreground hover:text-accent"
              >
                {child.label}
              </Link>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
